import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText, Calendar, DollarSign, Building, Download, CreditCard, MapPin, Receipt, Ticket, Users, Eye, Clock, Check, Monitor, Smartphone, Tablet, Link as LinkIcon } from 'lucide-react';
import api, { invoicesAPI } from '../services/api';
import { getSessionId } from '../utils/sessionTracking';
import { useFeatureFlag } from '../context/featureFlagStore';
import { useDisplay } from '../hooks/useDisplay';
import { formatDateTime, formatMoney } from '../utils/format';

// Format date helper
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  // Handle timezone issues for dates that come as UTC midnight
  // PostgreSQL returns dates as "2025-12-10T00:00:00.000Z" which shows as Dec 9 in EST
  let normalized = dateString;
  if (typeof dateString === 'string') {
    const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      normalized = dateMatch[1] + 'T12:00:00';
    }
  }
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Format currency helper
function formatCurrency(amount) {
  return formatMoney(amount || 0);
}

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromAdmin = searchParams.get('from') === 'admin';
  const [downloading, setDownloading] = useState(false);
  const canDownloadPdf = useFeatureFlag('feature.invoice_pdf');
  const canPayNow = useFeatureFlag('feature.invoice_pay');
  const d = useDisplay();


  // Engagement tracking refs
  const viewIdRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const scrolledToBottomRef = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const response = await invoicesAPI.get(id, {
        headers: { 'X-Session-Id': getSessionId() }
      });
      // Capture view ID from response header
      const viewId = response.headers?.['x-view-id'];
      if (viewId) {
        viewIdRef.current = viewId;
        startTimeRef.current = Date.now();
      }
      return response;
    },
  });

  const invoice = data?.data?.invoice;
  const customerViews = data?.data?.customerViews || [];
  const customerViewStats = data?.data?.customerViewStats || {};
  // Money returned on payments made against this invoice: amount and date.
  const refunds = data?.data?.refunds || [];

  // Track scroll to bottom
  useEffect(() => {
    const handleScroll = () => {
      if (scrolledToBottomRef.current) return;
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= docHeight - 100) {
        scrolledToBottomRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Send engagement data on unmount
  useEffect(() => {
    return () => {
      if (viewIdRef.current) {
        const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
        const data = JSON.stringify({
          timeOnPage,
          scrolledToBottom: scrolledToBottomRef.current
        });
        // Not sendBeacon: it can only POST, carries no Authorization header,
        // and the hard-coded /api path missed the API host on every tenant.
        // A keepalive fetch survives the unload and authenticates.
        fetch(`${api.defaults.baseURL}/invoices/views/${viewIdRef.current}/engagement`, {
          method: 'PATCH',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: api.defaults.headers.common['Authorization'] || '' },
          body: data,
        }).catch(() => {});
      }
    };
  }, []);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await invoicesAPI.getPdf(id);

      // Track the download
      if (viewIdRef.current) {
        invoicesAPI.trackPdfDownload(id, viewIdRef.current).catch(() => {});
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice?.invoiceNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download invoice PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return <div className="animate-fade-in p-8 text-center text-neutral-600 dark:text-neutral-400">Loading invoice...</div>;
  }

  if (!invoice) {
    return <div className="animate-fade-in p-8 text-center text-neutral-600 dark:text-neutral-400">Invoice not found</div>;
  }

  return (
    <div className="animate-fade-in">
      <button
        onClick={() => {
          const returnUrl = sessionStorage.getItem('invoicesReturnUrl') || '/invoices';
          sessionStorage.removeItem('invoicesReturnUrl');
          navigate(returnUrl, { state: { refresh: Date.now() } });
        }}
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to invoices
      </button>

      <div className="card mb-6">
        <div className="card-header">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-neutral-900 dark:text-white">Invoice #{invoice.invoiceNumber}</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className={`badge ${invoice.closedFlag ? 'badge-success' : 'badge-warning'}`}>
                {invoice.closedFlag ? 'Paid' : 'Open'}
              </span>
              {canDownloadPdf && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  className="btn-secondary btn-sm"
                >
                  <Download className="w-4 h-4" />
                  {downloading ? 'Downloading...' : 'Download PDF'}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {d.visible('invoiceDetail.summary.invoiceDate') && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.invoiceDate')}</p>
                <p className="font-medium text-neutral-900 dark:text-white">{formatDate(invoice.date)}</p>
              </div>
            </div>
            )}
            {d.visible('invoiceDetail.summary.dueDate') && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.dueDate')}</p>
                <p className="font-medium text-neutral-900 dark:text-white">{formatDate(invoice.dueDate)}</p>
              </div>
            </div>
            )}
            {d.visible('invoiceDetail.summary.totalAmount') && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.totalAmount')}</p>
                <p className="font-medium text-neutral-900 dark:text-white">{formatCurrency(invoice.total)}</p>
              </div>
            </div>
            )}
            {d.visible('invoiceDetail.summary.balanceDue') && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.summary.balanceDue')}</p>
                <p className={`font-medium ${invoice.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {formatCurrency(invoice.balance)}
                </p>
              </div>
            </div>
            )}
          </div>

          {/* Billing Information */}
          {d.visible('invoiceDetail.section.billingInfo') && (
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-4">{d.label('invoiceDetail.section.billingInfo')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {d.visible('invoiceDetail.billingInfo.billTo') && (
              <div className="flex items-start gap-3">
                <Building className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.billTo')}</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{invoice.billToCompany?.name || invoice.company?.name || 'N/A'}</p>
                </div>
              </div>
              )}
              {d.visible('invoiceDetail.billingInfo.address') && (invoice.billingSiteAddressLine1 || invoice.billingSiteCity) && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.address')}</p>
                    <p className="font-medium text-neutral-900 dark:text-white">
                      {invoice.billingSiteAddressLine1 && <span>{invoice.billingSiteAddressLine1}<br /></span>}
                      {invoice.billingSiteCity && `${invoice.billingSiteCity}, `}
                      {invoice.billingSiteState} {invoice.billingSiteZip}
                    </p>
                  </div>
                </div>
              )}
              {d.visible('invoiceDetail.billingInfo.terms') && invoice.billingTerms && (
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.terms')}</p>
                    <p className="font-medium text-neutral-900 dark:text-white">{invoice.billingTerms?.name || 'N/A'}</p>
                  </div>
                </div>
              )}
              {d.visible('invoiceDetail.billingInfo.customerPO') && invoice.customerPO && (
                <div className="flex items-start gap-3">
                  <Receipt className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.customerPO')}</p>
                    <p className="font-medium text-neutral-900 dark:text-white">{invoice.customerPO}</p>
                  </div>
                </div>
              )}
              {d.visible('invoiceDetail.billingInfo.reference') && invoice.reference && (
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.reference')}</p>
                    <p className="font-medium text-neutral-900 dark:text-white">{invoice.reference}</p>
                  </div>
                </div>
              )}
              {d.visible('invoiceDetail.billingInfo.relatedTicket') && invoice.ticket && (
                <div className="flex items-start gap-3">
                  <Ticket className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('invoiceDetail.billingInfo.relatedTicket')}</p>
                    <Link to={`/tickets/${invoice.ticket.id}`} className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                      #{invoice.ticket.id} - {invoice.ticket.summary}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Line Items / Billable Items */}
          {d.visible('invoiceDetail.section.billableItems') && invoice.lineItems && invoice.lineItems.length > 0 && (
            <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6 mt-6">
              <h3 className="font-medium text-neutral-900 dark:text-white mb-4">{d.label('invoiceDetail.section.billableItems')}</h3>

              {/* Group time entries by ticket for better display */}
              {(() => {
                const timeEntries = invoice.lineItems.filter(item => item.type === 'time');
                const otherItems = invoice.lineItems.filter(item => item.type !== 'time');

                // Group time entries by ticket
                const ticketGroups = {};
                timeEntries.forEach(entry => {
                  const key = entry.ticketId || 'no-ticket';
                  if (!ticketGroups[key]) {
                    ticketGroups[key] = {
                      ticketId: entry.ticketId,
                      ticketSummary: entry.ticketSummary || 'Service Time',
                      entries: [],
                      totalHours: 0,
                      totalAmount: 0
                    };
                  }
                  ticketGroups[key].entries.push(entry);
                  ticketGroups[key].totalHours += entry.quantity || 0;
                  ticketGroups[key].totalAmount += entry.extendedPrice || 0;
                });

                const ticketGroupArray = Object.values(ticketGroups);
                const hasTicketGroups = ticketGroupArray.length > 0;

                return (
                  <>
                    {/* Time Entries Grouped by Ticket */}
                    {hasTicketGroups && (
                      <div className="space-y-6 mb-6">
                        {ticketGroupArray.map((group, groupIndex) => (
                          <div key={group.ticketId || groupIndex} className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
                            {/* Ticket Header */}
                            <div className="bg-neutral-50 dark:bg-neutral-800 px-4 py-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Ticket className="w-4 h-4 text-primary-500" />
                                <span className="font-medium text-neutral-900 dark:text-white">
                                  {group.ticketSummary}
                                </span>
                                {group.ticketId && (
                                  <Link
                                    to={`/tickets/${group.ticketId}`}
                                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                  >
                                    (#{group.ticketId})
                                  </Link>
                                )}
                              </div>
                              <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                {group.totalHours.toFixed(2)} hrs • {formatCurrency(group.totalAmount)}
                              </div>
                            </div>

                            {/* Time Entries Table */}
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/50">
                                    <th className="text-left py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.time.col.date')}</th>
                                    <th className="text-left py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.time.col.member')}</th>
                                    <th className="text-left py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.time.col.notes')}</th>
                                    <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.time.col.hours')}</th>
                                    <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.time.col.rate')}</th>
                                    <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.time.col.amount')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.entries.map((entry, entryIndex) => (
                                    <tr key={entry.id || entryIndex} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                      <td className="py-3 px-4 text-neutral-700 dark:text-neutral-300 whitespace-nowrap align-top">
                                        {entry.date ? new Date(entry.date).toLocaleDateString() : 'N/A'}
                                      </td>
                                      <td className="py-3 px-4 text-neutral-700 dark:text-neutral-300 whitespace-nowrap align-top">
                                        {entry.member || 'N/A'}
                                      </td>
                                      <td className="py-3 px-4 text-neutral-600 dark:text-neutral-400 align-top">
                                        <div className="whitespace-pre-line max-w-md">
                                          {entry.notes || '-'}
                                        </div>
                                      </td>
                                      <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top whitespace-nowrap">
                                        {entry.quantity?.toFixed(2) || '0.00'}
                                      </td>
                                      <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top whitespace-nowrap">
                                        {formatCurrency(entry.unitPrice || 0)}/hr
                                      </td>
                                      <td className="py-3 px-4 text-right font-medium text-neutral-900 dark:text-white align-top whitespace-nowrap">
                                        {formatCurrency(entry.extendedPrice || 0)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Products, Expenses, and Other Items */}
                    {otherItems.length > 0 && (
                      <div className="overflow-x-auto">
                        {hasTicketGroups && (
                          <h4 className="font-medium text-neutral-900 dark:text-white mb-3">{d.label('invoiceDetail.products.header')}</h4>
                        )}
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-neutral-200 dark:border-neutral-700">
                              <th className="text-left py-2 pr-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.products.col.description')}</th>
                              <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400 whitespace-nowrap">{d.label('invoiceDetail.products.col.qty')}</th>
                              <th className="text-right py-2 px-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.products.col.rate')}</th>
                              <th className="text-right py-2 pl-4 font-medium text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.products.col.amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {otherItems.map((item, index) => (
                              <tr key={item.id || index} className="border-b border-neutral-100 dark:border-neutral-800">
                                <td className="py-3 pr-4">
                                  <div className="text-neutral-900 dark:text-white font-medium whitespace-pre-line">
                                    {item.description || 'Item'}
                                  </div>
                                  {item.serialNumber && (
                                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                                      S/N: {item.serialNumber}
                                    </div>
                                  )}
                                  {item.ticketId && (
                                    <Link
                                      to={`/tickets/${item.ticketId}`}
                                      className="text-xs text-primary-600 dark:text-primary-400 hover:underline mt-0.5 inline-block"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Ticket #{item.ticketId}
                                    </Link>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top">
                                  {item.quantity || 1}
                                </td>
                                <td className="py-3 px-4 text-right text-neutral-700 dark:text-neutral-300 align-top">
                                  {formatCurrency(item.unitPrice || 0)}
                                </td>
                                <td className="py-3 pl-4 text-right font-medium text-neutral-900 dark:text-white align-top">
                                  {formatCurrency(item.extendedPrice || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Invoice Breakdown */}
          {d.visible('invoiceDetail.section.breakdown') && (
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6 mt-6">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-4">{d.label('invoiceDetail.section.breakdown')}</h3>
            <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-4">
              <div className="space-y-2">
                {(invoice.serviceTotal > 0 || invoice.productTotal > 0 || invoice.expenseTotal > 0) && (
                  <>
                    {d.visible('invoiceDetail.breakdown.serviceTotal') && invoice.serviceTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.serviceTotal')}</span>
                        <span className="font-medium text-neutral-900 dark:text-white">{formatCurrency(invoice.serviceTotal)}</span>
                      </div>
                    )}
                    {d.visible('invoiceDetail.breakdown.productTotal') && invoice.productTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.productTotal')}</span>
                        <span className="font-medium text-neutral-900 dark:text-white">{formatCurrency(invoice.productTotal)}</span>
                      </div>
                    )}
                    {d.visible('invoiceDetail.breakdown.expenseTotal') && invoice.expenseTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.expenseTotal')}</span>
                        <span className="font-medium text-neutral-900 dark:text-white">{formatCurrency(invoice.expenseTotal)}</span>
                      </div>
                    )}
                    <div className="border-t border-neutral-200 dark:border-neutral-700 my-2"></div>
                  </>
                )}
                {d.visible('invoiceDetail.breakdown.subtotal') && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.subtotal')}</span>
                  <span className="font-medium text-neutral-900 dark:text-white">{formatCurrency(invoice.subtotal)}</span>
                </div>
                )}
                {d.visible('invoiceDetail.breakdown.salesTax') && invoice.salesTax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600 dark:text-neutral-400">{d.label('invoiceDetail.breakdown.salesTax')}</span>
                    <span className="font-medium text-neutral-900 dark:text-white">{formatCurrency(invoice.salesTax)}</span>
                  </div>
                )}
                <div className="border-t border-neutral-200 dark:border-neutral-700 my-2"></div>
                {d.visible('invoiceDetail.breakdown.total') && (
                <div className="flex justify-between">
                  <span className="font-medium text-neutral-900 dark:text-white">{d.label('invoiceDetail.breakdown.total')}</span>
                  <span className="font-bold text-neutral-900 dark:text-white">{formatCurrency(invoice.total)}</span>
                </div>
                )}
                {(invoice.payments > 0 || invoice.credits > 0 || refunds.length > 0) && (
                  <>
                    <div className="border-t border-neutral-200 dark:border-neutral-700 my-2"></div>
                    {d.visible('invoiceDetail.breakdown.paymentsApplied') && invoice.payments > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600 dark:text-green-400">{d.label('invoiceDetail.breakdown.paymentsApplied')}</span>
                        <span className="font-medium text-green-600 dark:text-green-400">-{formatCurrency(invoice.payments)}</span>
                      </div>
                    )}
                    {refunds.map((refund, i) => (
                      <div key={`refund-${i}`} className="flex justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">Refunded {formatDateTime(new Date(refund.date), { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                        <span className="font-medium text-neutral-700 dark:text-neutral-300">{formatCurrency(refund.amount)}</span>
                      </div>
                    ))}
                    {d.visible('invoiceDetail.breakdown.creditsApplied') && invoice.credits > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600 dark:text-green-400">{d.label('invoiceDetail.breakdown.creditsApplied')}</span>
                        <span className="font-medium text-green-600 dark:text-green-400">-{formatCurrency(invoice.credits)}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="border-t border-neutral-300 dark:border-neutral-600 my-2"></div>
                {d.visible('invoiceDetail.breakdown.balanceDue') && (
                <div className="flex justify-between">
                  <span className="font-bold text-neutral-900 dark:text-white">{d.label('invoiceDetail.breakdown.balanceDue')}</span>
                  <span className={`font-bold text-lg ${invoice.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(invoice.balance)}
                  </span>
                </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Pay Now Button for Open Invoices */}
          {canPayNow && !invoice.closedFlag && invoice.balance > 0 && (
            <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6 mt-6">
              <Link to={`/payments?invoice=${invoice.id}`} className="btn-primary">
                <CreditCard className="w-4 h-4" />
                Pay Now
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Customer Activity - Only visible to internal staff */}
      {fromAdmin && (customerViews.length > 0 || customerViewStats.totalCustomerViews > 0) && (
        <div className="card mt-6">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-500" />
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Customer Activity</h2>
                <span className="text-sm text-neutral-500 dark:text-neutral-400">(excludes internal staff views)</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-neutral-600 dark:text-neutral-300">
                <span><strong>{customerViewStats.totalCustomerViews || 0}</strong> views</span>
                <span><strong>{customerViewStats.uniqueCustomerViewers || 0}</strong> unique viewers</span>
              </div>
            </div>
          </div>
          <div className="card-body">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {customerViewStats.totalCustomerViews || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Total Customer Views</div>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="text-2xl font-bold text-neutral-900 dark:text-white">
                  {customerViewStats.uniqueCustomerViewers || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Unique Viewers</div>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold text-blue-600 dark:text-blue-400">
                  <Monitor className="w-5 h-5" />
                  {customerViewStats.portalViews || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Portal Views</div>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold text-purple-600 dark:text-purple-400">
                  <LinkIcon className="w-5 h-5" />
                  {customerViewStats.sharedLinkViews || 0}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Shared Link Views</div>
              </div>
            </div>

            {/* Activity Timestamps */}
            {(customerViewStats.firstCustomerView || customerViewStats.lastCustomerView) && (
              <div className="flex flex-wrap gap-6 mb-6 text-sm">
                {customerViewStats.firstCustomerView && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-neutral-400" />
                    <span className="text-neutral-500 dark:text-neutral-400">First viewed:</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formatDateTime(customerViewStats.firstCustomerView, {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
                {customerViewStats.lastCustomerView && (
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-neutral-400" />
                    <span className="text-neutral-500 dark:text-neutral-400">Last viewed:</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formatDateTime(customerViewStats.lastCustomerView, {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Detailed View History Table */}
            {customerViews.length > 0 && (
              <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
                <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-4">View History</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                        <th className="pb-2 font-medium">Date/Time</th>
                        <th className="pb-2 font-medium">Viewer</th>
                        <th className="pb-2 font-medium">Source</th>
                        <th className="pb-2 font-medium">Device</th>
                        <th className="pb-2 font-medium">Browser / OS</th>
                        <th className="pb-2 font-medium">Engagement</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                      {customerViews.map((view) => (
                        <tr key={view.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                          <td className="py-3 text-neutral-900 dark:text-white whitespace-nowrap">
                            {formatDateTime(view.viewedAt, {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="py-3">
                            <div className="text-neutral-700 dark:text-neutral-200">
                              {view.viewerName || 'Anonymous'}
                            </div>
                            {view.viewerEmail && (
                              <div className="text-neutral-500 dark:text-neutral-400 text-xs">{view.viewerEmail}</div>
                            )}
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              view.viewSource === 'portal_direct'
                                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                : 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
                            }`}>
                              {view.viewSource === 'portal_direct' ? (
                                <><Monitor className="w-3 h-3" /> Portal</>
                              ) : (
                                <><LinkIcon className="w-3 h-3" /> Shared</>
                              )}
                            </span>
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              view.deviceType === 'mobile' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' :
                              view.deviceType === 'tablet' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' :
                              'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                            }`}>
                              {view.deviceType === 'mobile' && <Smartphone className="w-3 h-3 mr-1" />}
                              {view.deviceType === 'tablet' && <Tablet className="w-3 h-3 mr-1" />}
                              {view.deviceType === 'desktop' && <Monitor className="w-3 h-3 mr-1" />}
                              {view.deviceType || 'desktop'}
                            </span>
                          </td>
                          <td className="py-3 text-neutral-600 dark:text-neutral-300 text-xs">
                            <div>{view.browser} {view.browserVersion}</div>
                            <div className="text-neutral-400 dark:text-neutral-500">{view.os} {view.osVersion}</div>
                          </td>
                          <td className="py-3">
                            <div className="flex flex-wrap gap-1">
                              {view.timeOnPage && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {Math.floor(view.timeOnPage / 60)}m {view.timeOnPage % 60}s
                                </span>
                              )}
                              {view.scrolledToBottom && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                                  <Check className="w-3 h-3 mr-1" /> Read all
                                </span>
                              )}
                              {view.pdfDownloaded && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                  <Download className="w-3 h-3 mr-1" /> PDF
                                </span>
                              )}
                              {!view.timeOnPage && !view.scrolledToBottom && !view.pdfDownloaded && (
                                <span className="text-neutral-400 dark:text-neutral-500">-</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {customerViews.length >= 50 && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-4">
                    Showing last 50 customer views
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
